export type ReceiptBillingDetails = {
  firstName: string;
  lastName: string;
  line1: string;
  city: string;
  country: string;
  region: string;
  postalCode: string;
  email: string;
  phone: string;
};

export type ReceiptSession = {
  merchantName: string;
  supportEmail?: string;
  description: string;
  amountMinor: string;
  currency: string;
  billing: ReceiptBillingDetails;
  reference: string;
  createdAt: string;
  testMode: boolean;
};

export const receiptStorageKey = "paypilot:receipt-details";

export function readReceiptSession() {
  try {
    const value = window.sessionStorage.getItem(receiptStorageKey);
    return value ? (JSON.parse(value) as ReceiptSession) : undefined;
  } catch {
    return undefined;
  }
}

function receiptMoney(amountMinor: string, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(Number(amountMinor) / 100);
}

export async function createReceiptPdf(
  receipt: ReceiptSession,
  reference: string,
  status: string,
  sessionId?: string | null,
) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const navy = [17, 27, 57] as const;
  const purple = [98, 87, 232] as const;
  const green = [20, 133, 87] as const;
  const muted = [105, 116, 143] as const;
  const line = [228, 232, 241] as const;
  const pageWidth = 210;
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;
  const formattedAmount = receiptMoney(receipt.amountMinor, receipt.currency);
  const issuedAt = new Date(receipt.createdAt);
  const billingName = `${receipt.billing.firstName} ${receipt.billing.lastName}`.trim();
  const billingAddress = [
    receipt.billing.line1,
    `${receipt.billing.city}, ${receipt.billing.region} ${receipt.billing.postalCode}`.trim(),
    receipt.billing.country,
  ].filter(Boolean);

  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 54, "F");
  doc.setFillColor(...purple);
  doc.roundedRect(margin, 14, 14, 14, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text("P", margin + 7, 23.4, { align: "center" });
  doc.setFontSize(16);
  doc.text("PayPilot", margin + 19, 21.8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(181, 190, 220);
  doc.text("SECURE PAYMENT RECEIPT", margin + 19, 27.2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(215, 222, 245);
  doc.text("PAYMENT RECEIPT", pageWidth - margin, 20, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    `Issued ${issuedAt.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}`,
    pageWidth - margin,
    26,
    { align: "right" },
  );

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...line);
  doc.roundedRect(margin, 42, contentWidth, 50, 4, 4, "FD");
  doc.setFillColor(233, 247, 241);
  doc.roundedRect(margin + 10, 51, 33, 9, 4.5, 4.5, "F");
  doc.setTextColor(...green);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(status.toUpperCase(), margin + 26.5, 56.8, { align: "center" });
  doc.setTextColor(...navy);
  doc.setFontSize(9);
  doc.text("TOTAL PAID", margin + 10, 69);
  doc.setFontSize(27);
  doc.text(formattedAmount, margin + 10, 82);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.text(
    receipt.testMode ? "Test transaction - no funds moved" : "Provider-confirmed customer payment",
    pageWidth - margin - 10,
    78,
    { align: "right" },
  );

  const sectionTitle = (title: string, y: number) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...purple);
    doc.text(title.toUpperCase(), margin, y);
    doc.setDrawColor(...line);
    doc.line(margin, y + 3, pageWidth - margin, y + 3);
  };
  const field = (label: string, value: string, x: number, y: number, maxWidth = 74) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...navy);
    const lines = doc.splitTextToSize(value || "-", maxWidth) as string[];
    doc.text(lines, x, y + 5);
  };

  sectionTitle("Receipt information", 105);
  field("Payment reference", reference, margin, 115, 78);
  field(
    "Payment date",
    issuedAt.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }),
    112,
    115,
    80,
  );
  field(
    "Payment method",
    receipt.testMode ? "PayPilot test simulation" : "Secure provider-hosted checkout",
    margin,
    132,
    78,
  );
  field("Currency", receipt.currency.toUpperCase(), 112, 132, 80);
  if (sessionId) field("Provider session", sessionId, margin, 149, 164);

  sectionTitle("Order details", 160);
  field("Description", receipt.description, margin, 170, 112);
  field("Amount", formattedAmount, 142, 170, 50);

  sectionTitle("Billing details", 190);
  field("Customer", billingName, margin, 200, 78);
  field("Email", receipt.billing.email, 112, 200, 80);
  field("Billing address", billingAddress.join("\n"), margin, 216, 78);
  field("Phone", receipt.billing.phone, 112, 216, 80);

  sectionTitle("Merchant", 236);
  field("Paid to", receipt.merchantName, margin, 246, 78);
  field("Support", receipt.supportEmail || "Contact the merchant", 112, 246, 80);

  doc.setFillColor(247, 249, 252);
  doc.roundedRect(margin, 268, contentWidth, 12, 3, 3, "F");
  doc.setTextColor(...muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("This receipt was generated by PayPilot. Keep it for your records.", margin + 6, 275.5);
  doc.text("Page 1 of 1", pageWidth - margin - 6, 275.5, { align: "right" });
  doc.setFontSize(7);
  doc.text("Encrypted checkout  |  Payment details protected", pageWidth / 2, 288, { align: "center" });

  const filename = `PayPilot-receipt-${reference.replace(/[^a-zA-Z0-9_-]/g, "-")}.pdf`;
  return { doc, filename };
}

export async function downloadReceiptPdf(
  receipt: ReceiptSession,
  reference: string,
  status: string,
  sessionId?: string | null,
) {
  const { doc, filename } = await createReceiptPdf(receipt, reference, status, sessionId);
  doc.save(filename);
}
